import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Grid,
  Checkbox,
} from "@mui/material";
import apiClient from "../../../utils/apiClient";
import escapeHtml from "lodash/escape";
import Swal from "../../../utils/swal";
import DashboardTitleComponent from "../../custom/DashboardTitleComponent";
import CustomButton from "../../custom/CustomButton";
import CustomTextField from "../../custom/CustomTextField";
import RowActionsMenu from "../../custom/RowActionsMenu";
import CustomNumberField from "../../custom/CustomNumberField";
import CustomAutocompleteField from "../../custom/CustomAutoCompleteField";
import IconPickerField from "../../custom/IconPickerField";
import MenuReorderList from "./MenuReorderList";
import MenuRolesModal from "./MenuRolesModal";
import { resolveMenuIcon } from "../../../utils/menuIcons";
import { showSuccessToast, showErrorToast } from "../../../utils/swalToast";

// Sentinel for "no parent" — safe since real Menu PKs start at 1.
const NO_PARENT = 0;

// Fix z-index so Swal renders above an open MUI Dialog (1300 > swal2's ~1060).
const swalZIndexFix = {
  didOpen: () => {
    const container = document.querySelector(".swal2-container");
    const popup = document.querySelector(".swal2-popup");
    if (container) container.style.zIndex = "9999";
    if (popup) popup.style.zIndex = "99999";
  },
};

const emptyForm = {
  label: "",
  path: "",
  icon: null,
  order: "",
  parentId: NO_PARENT,
  roleIds: [],
};

const MenuManagementMenu = () => {
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);

  const [reorderMode, setReorderMode] = useState(false);
  const [reorderState, setReorderState] = useState({});
  const [savingReorder, setSavingReorder] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [openRolesModal, setOpenRolesModal] = useState(false);
  const [rolesTargetMenu, setRolesTargetMenu] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get("/access-control/menus"),
      apiClient.get("/access-control/roles"),
    ])
      .then(([menusRes, rolesRes]) => {
        setMenus(menusRes.data.data || []);
        setRoles(rolesRes.data.data || []);
      })
      .catch((error) => console.error("Error fetching menus", error))
      .finally(() => setLoading(false));
  }, [refresh]);

  const byParent = useMemo(() => {
    const map = new Map();
    menus.forEach((menu) => {
      const parentId = menu.parent_id || null;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(menu);
    });
    return map;
  }, [menus]);

  const rows = useMemo(() => {
    const flatten = (parentId, depth) =>
      (byParent.get(parentId) || []).flatMap((menu) => [
        { ...menu, depth },
        ...flatten(menu.id, depth + 1),
      ]);
    return flatten(null, 0).map((menu, index) => ({ ...menu, no: index + 1 }));
  }, [byParent]);

  const descendantIds = (menuId) => {
    const ids = [];
    const walk = (parentId) => {
      (byParent.get(parentId) || []).forEach((menu) => {
        ids.push(menu.id);
        walk(menu.id);
      });
    };
    walk(menuId);
    return ids;
  };

  const parentOptions = (excludeId) => {
    const excluded = new Set(excludeId ? [excludeId, ...descendantIds(excludeId)] : []);
    return [
      { id: NO_PARENT, label: "Tidak ada (menu utama)" },
      ...menus.filter((menu) => !excluded.has(menu.id)).map((menu) => ({ id: menu.id, label: menu.label })),
    ];
  };

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setOpenModal(true);
  };

  const handleOpenEdit = (menu) => {
    setIsEditMode(true);
    setSelectedId(menu.id);
    setForm({
      label: menu.label,
      path: menu.path || "",
      icon: menu.icon,
      order: menu.order ?? "",
      parentId: menu.parent_id || NO_PARENT,
      roleIds: menu.roleIds || [],
    });
    setOpenModal(true);
  };

  const handleClose = () => setOpenModal(false);

  const toggleFormRole = (roleId) => {
    setForm((f) => ({
      ...f,
      roleIds: f.roleIds.includes(roleId)
        ? f.roleIds.filter((id) => id !== roleId)
        : [...f.roleIds, roleId],
    }));
  };

  const handleOpenRoles = (menu) => {
    setRolesTargetMenu(menu);
    setOpenRolesModal(true);
  };

  const handleRolesSaved = () => {
    setOpenRolesModal(false);
    setRefresh((prev) => !prev);
  };

  const handleSubmit = async () => {
    if (!form.label.trim()) {
      return showErrorToast("Nama menu wajib diisi");
    }
    if (!isEditMode && form.roleIds.length === 0) {
      return showErrorToast(
        "Pilih setidaknya satu role, kalau tidak menu ini tidak akan terlihat oleh siapa pun"
      );
    }

    const payload = {
      label: form.label.trim(),
      path: form.path.trim() || null,
      icon: form.icon || null,
      order: form.order === "" ? undefined : Number(form.order),
      parent_id: form.parentId === NO_PARENT ? null : form.parentId,
      roleIds: form.roleIds,
    };

    try {
      if (isEditMode) {
        await apiClient.put(`/access-control/menus/${selectedId}`, payload);
      } else {
        await apiClient.post("/access-control/menus", payload);
      }
      showSuccessToast(`Menu berhasil ${isEditMode ? "diperbaharui" : "ditambahkan"}`);
      setOpenModal(false);
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  const handleDelete = async (menu) => {
    const hasChildren = menus.some((m) => m.parent_id === menu.id);
    if (hasChildren) {
      return showErrorToast("Menu masih memiliki sub-menu, hapus sub-menu terlebih dahulu");
    }

    const result = await Swal.fire({
      title: "Konfirmasi",
      html: `Hapus menu <b>${escapeHtml(menu.label)}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      ...swalZIndexFix,
    });
    if (!result.isConfirmed) return;

    try {
      const response = await apiClient.delete(`/access-control/menus/${menu.id}`);
      showSuccessToast(response.data.msg || "Menu berhasil dihapus");
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  // Reorder mode: edited locally, committed on "Simpan Urutan". parentKey
  // "root" is the top-level list; otherwise it's String(parentId).

  const rootItemsBase = useMemo(
    () =>
      (byParent.get(null) || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((m) => ({ id: m.id, label: m.label })),
    [byParent]
  );

  const childGroupsBase = useMemo(() => {
    const map = {};
    byParent.forEach((items, parentId) => {
      if (!parentId) return;
      map[parentId] = items
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((m) => ({ id: m.id, label: m.label }));
    });
    return map;
  }, [byParent]);

  const applyOverride = (parentKey, baseItems) => {
    const overrideIds = reorderState[parentKey];
    if (!overrideIds) return baseItems;
    const byId = new Map(baseItems.map((item) => [item.id, item]));
    return overrideIds.map((id) => byId.get(id)).filter(Boolean);
  };

  // Overlay in-progress local drag results so a second drag continues from the latest state.
  const displayRootItems = useMemo(
    () => applyOverride("root", rootItemsBase),
    [rootItemsBase, reorderState]
  );

  const displayChildGroups = useMemo(() => {
    const map = {};
    Object.entries(childGroupsBase).forEach(([parentId, items]) => {
      map[parentId] = applyOverride(String(parentId), items);
    });
    return map;
  }, [childGroupsBase, reorderState]);

  const handleEnterReorder = () => {
    setReorderState({});
    setReorderMode(true);
  };

  const handleCancelReorder = () => {
    setReorderState({});
    setReorderMode(false);
  };

  const handleRootReorder = (orderedIds) => {
    setReorderState((prev) => ({ ...prev, root: orderedIds }));
  };

  const handleChildReorder = (parentId, orderedIds) => {
    setReorderState((prev) => ({ ...prev, [String(parentId)]: orderedIds }));
  };

  const handleSaveReorder = async () => {
    const changedGroups = Object.entries(reorderState);
    if (changedGroups.length === 0) {
      setReorderMode(false);
      return;
    }

    setSavingReorder(true);
    try {
      await Promise.all(
        changedGroups.map(([parentKey, orderedIds]) => {
          const parentId = parentKey === "root" ? null : Number(parentKey);
          return apiClient.put("/access-control/menus/reorder", {
            parent_id: parentId,
            orderedIds,
          });
        })
      );
      showSuccessToast("Urutan menu berhasil disimpan");
      setReorderState({});
      setReorderMode(false);
      setRefresh((prev) => !prev);
    } catch (error) {
      showErrorToast(error?.response?.data?.msg || "Terjadi kesalahan");
    } finally {
      setSavingReorder(false);
    }
  };

  if (loading) {
    return (
      <div>
        <DashboardTitleComponent text={"Menu Settings"} />
        <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={130} height={36} />
          <Skeleton variant="rounded" width={130} height={36} />
        </Box>
        <Box sx={{ width: "100%", overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {["No.", "Menu", "Path", "Urutan", "Aksi"].map((h) => (
                  <TableCell key={h}>
                    <Skeleton width="60%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 8 }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: 5 }).map((__, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton width={colIndex === 1 ? `${70 - rowIndex * 3}%` : "50%"} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <DashboardTitleComponent text={"Menu Settings"} />

      <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
        {reorderMode ? (
          <>
            <CustomButton
              onClick={handleSaveReorder}
              text={savingReorder ? "Menyimpan..." : "Simpan Urutan"}
              isFullWidth={false}
              baseColor="#003158"
              hoverColor="#00213A"
              disabled={savingReorder}
            />
            <CustomButton
              onClick={handleCancelReorder}
              text="Batal"
              isFullWidth={false}
              baseColor="#A92027"
              hoverColor="#8C1016"
              disabled={savingReorder}
            />
          </>
        ) : (
          <>
            <CustomButton
              onClick={handleEnterReorder}
              text="Edit Urutan"
              isFullWidth={false}
              baseColor="#003158"
              hoverColor="#00213A"
            />
            <CustomButton
              onClick={handleOpenAdd}
              text="Tambah Menu"
              isFullWidth={false}
              baseColor="#003158"
              hoverColor="#00213A"
            />
          </>
        )}
      </Box>

      {reorderMode ? (
        <MenuReorderList
          rootItems={displayRootItems}
          childGroupsByParentId={displayChildGroups}
          onRootReorder={handleRootReorder}
          onChildReorder={handleChildReorder}
        />
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <Table
            sx={{
              minWidth: 700,
              "& .MuiTableCell-root": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiTableCell-head": {
                backgroundColor: "#fafafa",
                color: "#666",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell width={60}>No.</TableCell>
                <TableCell>Menu</TableCell>
                <TableCell>Path</TableCell>
                <TableCell width={100}>Urutan</TableCell>
                <TableCell width={80}>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.no}</TableCell>
                  <TableCell sx={{ pl: row.depth * 3 + 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {resolveMenuIcon(row.icon)}
                      <Typography fontWeight={row.depth === 0 ? 600 : 400}>{row.label}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.path || "-"}</TableCell>
                  <TableCell>{row.order}</TableCell>
                  <TableCell>
                    <RowActionsMenu
                      actions={[
                        { label: "Edit", onClick: () => handleOpenEdit(row) },
                        { label: "Kelola Akses Menu", onClick: () => handleOpenRoles(row) },
                        { label: "Hapus", onClick: () => handleDelete(row), danger: true },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? "Edit Menu" : "Tambah Menu"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                Nama Menu<span style={{ color: "red" }}>*</span>
              </Typography>
              <CustomTextField
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>Path (opsional)</Typography>
              <CustomTextField
                placeholder="/contoh/path"
                value={form.path}
                onChange={(e) => setForm((f) => ({ ...f, path: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <IconPickerField
                value={form.icon}
                onChange={(name) => setForm((f) => ({ ...f, icon: name }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>Urutan (opsional)</Typography>
              <CustomNumberField
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>Parent Menu (opsional)</Typography>
              <CustomAutocompleteField
                value={form.parentId}
                onChange={(newValue) =>
                  setForm((f) => ({ ...f, parentId: newValue ? newValue.id : NO_PARENT }))
                }
                options={parentOptions(isEditMode ? selectedId : null)}
                searchBy="id"
                displayField="label"
                placeholder="Pilih Parent Menu"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                Role yang boleh melihat menu ini<span style={{ color: "red" }}>*</span>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {roles.map((role) => (
                  <Box
                    key={role.id}
                    onClick={() => toggleFormRole(role.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      pr: 1.5,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(0,49,88,0.04)" },
                    }}
                  >
                    <Checkbox
                      checked={form.roleIds.includes(role.id)}
                      onChange={() => toggleFormRole(role.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography variant="body2">{role.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <CustomButton
                onClick={handleSubmit}
                text={isEditMode ? "Update" : "Tambah"}
                isFullWidth={false}
                baseColor="#003158"
                hoverColor="#00213A"
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <MenuRolesModal
        open={openRolesModal}
        onClose={() => setOpenRolesModal(false)}
        menu={rolesTargetMenu}
        roles={roles}
        onSuccess={handleRolesSaved}
      />
    </div>
  );
};

export default MenuManagementMenu;
